// The game store editor (spec/dashboard.md section 7.5): store chips with the default star, the pages as tabs, the slots
// of the chosen page as cards with their type ribbon, condition badge and logic summaries; pages, slots and logics are
// component documents owned by the store, every edit staged through the workspace. M7 slice 7: "Show store analytics"
// reveals per slot the purchases and buyers (total / 30 d / 7 d) of the selected environment from the `slot` series, keyed
// by the slot's store item (the SDK's liveops.slot_purchased names the item). Section 7.5's handles: the page tabs sort by
// drag (horizontal), the slot cards sort within the page and drop onto another page's tab; every drop stages what the
// menu's Move items stage (movePage / moveSlot / moveSlotToPage: one whole-list edit each), so undo restores the order.
// Section 7.5's slot editor in place on the card: the condition in words with Edit (the builder inline, the slot's
// `condition` parameter the owner) and Clear; the logic as chips, one per owned SlotLogic* component with its sentence
// (spec/liveops-engine.md section 8.2: cooldown, period limit; section 8.3: the first-purchase multiplier), ✕ to remove,
// a click for the component's fields, "Add logic" with + Cooldown / + Period / + First purchase bonus; the Type ribbon
// as a chip with the enum select; price and reward read-only (the store item's) with "Edit the store item". View and
// Tags stay in the ⋮ menu's Edit slot (the generic editor).
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, pointerWithin, useSensor, useSensors, type CollisionDetection, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, rectSortingStrategy, sortableKeyboardCoordinates, useSortable, type SortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link, useParams } from 'react-router';
import type { Document, Json, Parameter } from '../../api/types';
import { useContent } from '../../content/ContentContext';
import { DocumentEditorHost } from '../../content/DocumentEditor';
import { Chip } from '../../content/cards/Chips';
import { ConditionField, ConditionSummary } from '../../content/conditions/ConditionBuilder';
import { ComponentField } from '../../content/widgets/ComponentField';
import { FieldWidget } from '../../content/widgets/FieldWidget';
import { formatValue } from '../../content/format';
import { n, useDefinitionText, useEntityFigures, type EntityFigures } from '../../content/liveops/analytics';
import { EnvSelector } from '../../content/liveops/env';
import { useDocumentList } from '../../content/useDocumentList';
import { ReferencePicker } from '../../content/widgets/ReferenceField';
import { createDocumentDraft, heirsOf, useEditorHost, useWidgetEnv } from '../../content/widgets/context';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { Banners, EmptyState } from '../../ui/Banner';
import { Button, IconButton } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { useConfirm } from '../../ui/ConfirmDialog';
import { ChevronDown, ExternalLink, Filter, GripVertical, LayoutGrid, MoreHorizontal, MoreVertical, Pencil, Plus, Settings, Star, Store, Table2, X } from '../../ui/icons';
import { Select, Switch } from '../../ui/inputs';
import { Menu, Popover } from '../../ui/Popover';
import { formatDuration } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { useWorkspaceVersion } from '../../workspace/react';
import { entityKey, isTempId } from '../../workspace/Workspace';
import { IconThumb, NameText, STORE_ITEM } from './cards';
import { RangePicker } from './RangePicker';
import './liveops.css';

const STORE_BASE = 'SmartObjects.GameStoreBase';
const SMART_CONFIG = 'SmartObjects.SmartConfig';
const PAGE = 'LiveOps.Store.Page';
const SLOT = 'LiveOps.Store.Slot';
const SLOT_TYPES = ['Default', 'Popular', 'Best', 'New', 'Great', 'Limited', 'Once', 'Free'];

type Data = Record<string, Json>;
type DragKind = 'page' | 'slot';

const kindOf = (data: { current?: unknown } | undefined): DragKind | undefined => (data?.current as { kind?: DragKind } | undefined)?.kind;

/** A slot hovering a page tab leaves the slot cards where they are. */
const slotStrategy: SortingStrategy = (args) => (args.overIndex < 0 || args.activeIndex < 0 ? null : rectSortingStrategy(args));

/**
 * The store's collision rule: a page drags among the page tabs only; a slot drops onto the tab under the pointer when there
 * is one (a move to that page), otherwise onto the closest slot (the keyboard sensor has no pointer: the closest slot or tab).
 */
const storeCollision: CollisionDetection = (args) => {
  const tabs = args.droppableContainers.filter((c) => kindOf(c.data) === 'page');
  if (kindOf(args.active.data) === 'page') return closestCenter({ ...args, droppableContainers: tabs });
  if (args.pointerCoordinates) {
    const onTab = pointerWithin({ ...args, droppableContainers: tabs });
    if (onTab.length > 0) return onTab;
    return closestCenter({ ...args, droppableContainers: args.droppableContainers.filter((c) => !tabs.includes(c)) });
  }
  return closestCenter(args);
};

export function StorePage() {
  return (
    <DocumentEditorHost>
      <StoreEditor />
    </DocumentEditorHost>
  );
}

function ids(value: Json | undefined): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function StoreEditor() {
  const { org, game, can } = useGame();
  const { branch = '' } = useParams();
  const catalog = useContent();
  const { workspace } = useWidgetEnv();
  const version = useWorkspaceVersion(workspace);
  const host = useEditorHost();
  const confirm = useConfirm();
  const toasts = useToasts();
  const canWrite = can('content_write') && !workspace.isProtected;
  const storeTemplates = useMemo(() => [catalog.templates.get(SMART_CONFIG), ...heirsOf(catalog, STORE_BASE).filter((t) => t.name !== SMART_CONFIG)].filter((t): t is NonNullable<typeof t> => !!t && !t.flags.includes('abstract')), [catalog]);
  const [templateName, setTemplateName] = useState(SMART_CONFIG);
  const storeTemplate = catalog.templates.get(templateName);
  const list = useDocumentList({ template: templateName, includeHeirs: false, sort: { key: 'id', dir: 'asc' } });
  const stores = list.rows;
  const stagedDefault = (workspace.getDraft(entityKey('template-selected', templateName))?.current as { selected?: string | null } | undefined)?.selected;
  const defaultId = stagedDefault !== undefined ? stagedDefault : (storeTemplate?.selected ?? null);
  const [storeId, setStoreId] = useState<string | null>(null);
  const store = stores.find((s) => s.id === storeId) ?? stores.find((s) => s.id === defaultId) ?? stores[0] ?? null;
  const pageIds = ids((store?.data as Data | undefined)?.store);
  const [pageIndex, setPageIndex] = useState(0);
  const pageId = pageIds[Math.min(pageIndex, Math.max(0, pageIds.length - 1))] ?? null;
  const page = pageId ? (catalog.resolve(pageId) as Document | undefined) : undefined;
  const slotIds = ids((page?.data as Data | undefined)?.content);
  const [pickingSlot, setPickingSlot] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [dragKind, setDragKind] = useState<DragKind | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  useEffect(() => {
    void catalog.resolver.load([...pageIds, ...slotIds]);
  }, [catalog.resolver, pageIds.join(','), slotIds.join(','), workspace.head]); // eslint-disable-line react-hooks/exhaustive-deps
  // the slots' logic components and condition roots (the chips and the condition sentence read them)
  const logicIds = useMemo(() => slotIds.flatMap((id) => { const d = (catalog.resolve(id)?.data ?? {}) as Data; return [...ids(d.logic), ...ids([d.condition ?? null])]; }), [slotIds, catalog, version]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    void catalog.resolver.load(logicIds);
  }, [catalog.resolver, logicIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const serverOf = (id: string): Document | undefined => catalog.resolver.get(id) as Document | undefined;
  const edit = (doc: Document, path: string, value: unknown) => {
    const server = isTempId(doc.id) ? undefined : serverOf(doc.id);
    workspace.edit('document', doc.id, path, value, { base: server ? { version: Number(server.version), value: server as unknown as Json } : undefined, label: catalog.label(doc), template: doc.template });
  };
  const current = (id: string): Document | undefined => (workspace.getDraft(entityKey('document', id))?.current as unknown as Document | undefined) ?? serverOf(id) ?? (catalog.resolve(id) as Document | undefined);
  const slotItems = useMemo(() => (showAnalytics ? slotIds.map((id) => ((current(id)?.data ?? {}) as Data).storeItem).filter((v): v is string => typeof v === 'string') : []), [showAnalytics, slotIds.join(','), version]); // eslint-disable-line react-hooks/exhaustive-deps
  const slotFigures = useEntityFigures('slot', slotItems);
  const remove = (id: string) => {
    const doc = current(id);
    if (!doc) return;
    if (isTempId(id)) {
      workspace.remove('document', id);
      return;
    }
    const server = serverOf(id);
    workspace.remove('document', id, server ? { version: Number(server.version), value: server as unknown as Json } : undefined, { label: catalog.label(doc), template: doc.template });
  };

  const markDefault = (id: string) => {
    if (!storeTemplate) return;
    workspace.edit('template-selected', templateName, 'selected', id, { base: { version: Number(storeTemplate.version), value: { selected: storeTemplate.selected ?? null } as unknown as Json }, label: `Default ${storeTemplate.displayName ?? templateName}` });
  };
  const createStore = () => {
    if (!storeTemplate) return;
    const id = createDocumentDraft(workspace, storeTemplate, { store: [], priority: 100 });
    setStoreId(id);
    void host?.open(id);
  };
  const addPage = () => {
    const def = catalog.templates.get(PAGE);
    if (!store || !def) return;
    const id = createDocumentDraft(workspace, def, { content: [] });
    edit(store, 'data.store', [...pageIds, id]);
    setPageIndex(pageIds.length);
  };
  const movePage = (from: number, to: number) => {
    if (!store || to < 0 || to >= pageIds.length) return;
    const next = [...pageIds];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    edit(store, 'data.store', next);
    // the chosen page stays chosen (the menu moves the chosen page; a drag may move another one)
    setPageIndex(pageId ? next.indexOf(pageId) : to);
  };
  const deletePage = async (id: string) => {
    if (!store) return;
    const p = current(id);
    const slots = ids((p?.data as Data | undefined)?.content);
    const ok = await confirm({ title: `Delete page ${p ? catalog.label(p) : id}?`, body: `${slots.length} slot${slots.length === 1 ? '' : 's'} and their logic are deleted with it (staged; Save All commits).`, confirmLabel: 'Delete page', tone: 'danger' });
    if (!ok) return;
    edit(store, 'data.store', pageIds.filter((x) => x !== id));
    for (const s of slots) deleteSlotDocs(s);
    remove(id);
    setPageIndex((i) => Math.max(0, Math.min(i, pageIds.length - 2)));
  };
  const deleteSlotDocs = (slotId: string) => {
    const s = current(slotId);
    for (const l of ids((s?.data as Data | undefined)?.logic)) remove(l);
    remove(slotId);
  };
  const addSlot = (storeItemId: string) => {
    const def = catalog.templates.get(SLOT);
    if (!page || !def) return;
    const id = createDocumentDraft(workspace, def, { storeItem: storeItemId, type: 0, logic: [] });
    edit(page, 'data.content', [...slotIds, id]);
  };
  const moveSlot = (from: number, to: number) => {
    if (!page || to < 0 || to >= slotIds.length) return;
    const next = [...slotIds];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    edit(page, 'data.content', next);
  };
  const moveSlotToPage = (slotId: string, targetPageId: string) => {
    const target = current(targetPageId);
    if (!page || !target) return;
    edit(page, 'data.content', slotIds.filter((x) => x !== slotId));
    edit(target, 'data.content', [...ids((target.data as Data).content), slotId]);
    toasts.info(`Slot moved to ${catalog.label(target)}.`);
  };
  const deleteSlot = async (slotId: string) => {
    if (!page) return;
    const s = current(slotId);
    const ok = await confirm({ title: `Remove slot ${s ? catalog.label(s) : slotId}?`, body: 'The slot and its logic are deleted (staged; Save All commits).', confirmLabel: 'Remove slot', tone: 'danger' });
    if (!ok) return;
    edit(page, 'data.content', slotIds.filter((x) => x !== slotId));
    deleteSlotDocs(slotId);
  };
  const storeParams = storeTemplate?.effectiveParameters ?? [];
  const chip = (doc: Document, name: string, zero?: string) => {
    const p = storeParams.find((x) => x.name === name);
    return p ? <Chip key={name} doc={doc} param={p} onCommit={(v) => edit(doc, `data.${name}`, v)} disabled={!canWrite} zeroMeans={zero} /> : null;
  };
  const banners = [];
  if (storeTemplate && stores.length > 0 && !defaultId) banners.push({ id: 'no-default', tone: 'warning' as const, body: 'No default store: mark one with its star button (the engine resolves the singleton through its default when no condition decides).' });
  if (list.error) banners.push({ id: 'err', tone: 'danger' as const, body: list.error.message });

  const pageMenuItems = (id: string, i: number) => [
    { key: 'edit', label: 'Edit page', icon: <Pencil aria-hidden="true" />, onSelect: () => void host?.open(id) },
    { key: 'left', label: 'Move page left', onSelect: () => movePage(i, i - 1), disabled: !canWrite || i === 0 },
    { key: 'right', label: 'Move page right', onSelect: () => movePage(i, i + 1), disabled: !canWrite || i === pageIds.length - 1 },
    { key: 's', label: '', separator: true },
    { key: 'delete', label: 'Delete page', onSelect: () => void deletePage(id), danger: true, disabled: !canWrite },
  ];
  // a drop stages exactly what the menu's Move items stage
  const onDragStart = (e: DragStartEvent) => setDragKind(kindOf(e.active.data) ?? null);
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setDragKind(null);
    if (!over || active.id === over.id || !canWrite) return;
    const kind = kindOf(active.data);
    const overKind = kindOf(over.data);
    const a = String(active.id);
    const o = String(over.id);
    if (kind === 'page' && overKind === 'page') movePage(pageIds.indexOf(a), pageIds.indexOf(o));
    else if (kind === 'slot' && overKind === 'slot') moveSlot(slotIds.indexOf(a), slotIds.indexOf(o));
    else if (kind === 'slot' && overKind === 'page' && o !== pageId) moveSlotToPage(a, o);
  };
  const condOf = (doc: Document): string | null => (typeof (doc.data as Data).condition === 'string' ? ((doc.data as Data).condition as string) : null);

  return (
    <section className="store-page lo-store">
      <div className="page-head">
        <h1>Game store</h1>
        {storeTemplates.length > 1 && (
          <Select size="s" className="input-inline" value={templateName} onChange={(e) => { setTemplateName(e.target.value); setStoreId(null); setPageIndex(0); }} aria-label="Store template">
            {storeTemplates.map((t) => (
              <option key={t.name} value={t.name}>
                {t.displayName ?? t.name}
              </option>
            ))}
          </Select>
        )}
        {list.total !== null && (
          <span className="page-meta tabular">
            {stores.length} store{stores.length === 1 ? '' : 's'}
          </span>
        )}
        <span className="spacer" />
        <Link className="btn btn-ghost btn-s" to={`${routes.branch(org, game.slug, branch, 'liveops/store')}?dialog=liveops-settings`} title="LiveOps settings: the comfort payment">
          <Settings aria-hidden="true" /> Settings
        </Link>
        <Button size="s" variant="primary" onClick={createStore} missingPermission={can('content_write') ? null : 'content_write'} disabled={workspace.isProtected || !storeTemplate} icon={<Plus aria-hidden="true" />}>
          Create store
        </Button>
      </div>
      <div className="page-toolbar">
        {stores.length > 0 && (
          <div className="lo-stores" role="tablist" aria-label="Stores">
            {stores.map((s) => {
              const active = store?.id === s.id;
              const isDefault = defaultId === s.id;
              return (
                <span key={s.id} className="lo-store-item" role="tab" aria-selected={active}>
                  <button type="button" className="lo-store-name" onClick={() => { setStoreId(s.id); setPageIndex(0); }}>
                    <NameText doc={s} />
                  </button>
                  <IconButton className={isDefault ? 'lo-star-on' : ''} label={isDefault ? 'The default store' : 'Make this the default store'} onClick={() => canWrite && markDefault(s.id)} disabled={!canWrite || isTempId(s.id)} title={isTempId(s.id) ? 'Save the store first' : undefined}>
                    {isDefault ? <Star fill="currentColor" aria-hidden="true" /> : <Star aria-hidden="true" />}
                  </IconButton>
                  <IconButton label={`Edit store ${catalog.label(s)}`} onClick={() => void host?.open(s.id)}>
                    <Pencil aria-hidden="true" />
                  </IconButton>
                </span>
              );
            })}
          </div>
        )}
        <span className="spacer" />
        {showAnalytics && (
          <span className="lo-store-analytics" data-testid="store-analytics-bar" title="Purchases and buyers per slot from liveops.slot_purchased (by store item); revenue is the calendar's revenue row.">
            <EnvSelector />
            <RangePicker testers />
          </span>
        )}
        <Switch label="Show store analytics" checked={showAnalytics} onChange={setShowAnalytics} />
        <Link className="btn btn-ghost btn-s" to={routes.documents(org, game.slug, branch, templateName)}>
          <Table2 aria-hidden="true" /> Table view
        </Link>
      </div>
      <Banners messages={banners} />
      {stores.length === 0 && list.loading && (
        <div className="pane">
          <div className="skeleton-rows" role="status" aria-label="Loading the stores">
            {[0, 1, 2].map((k) => (
              <div key={k} className="skeleton-row">
                <span className="skeleton" />
                <span className="skeleton" />
              </div>
            ))}
          </div>
        </div>
      )}
      {stores.length === 0 && !list.loading && !list.error && (
        <EmptyState
          icon={<Store aria-hidden="true" />}
          title="No store yet"
          body="A store holds pages of slots, each slot selling one store item; the default store is the one the game opens."
          action={
            <Button size="s" onClick={createStore} disabled={!canWrite || !storeTemplate} icon={<Plus aria-hidden="true" />}>
              New store
            </Button>
          }
        />
      )}
      {store && (
        <div className="pane lo-store-pane">
          <div className="pane-head">
            <div className="lo-facts" aria-label="Store settings">
              {chip(store, 'offerProducts')}
              {chip(store, 'multipliedStoreItems')}
              {chip(store, 'priority')}
              <span className="label-value">
                <span className="label">Condition</span>
                <span className="lo-cond">
                  <Filter aria-hidden="true" />
                  <ConditionSummary rootId={condOf(store)} />
                </span>
              </span>
            </div>
          </div>
          <div className="pane-body">
            <DndContext sensors={sensors} collisionDetection={storeCollision} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setDragKind(null)}>
            <div className="tabs lo-page-tabs" role="tablist" aria-label="Store pages">
              <SortableContext items={pageIds} strategy={horizontalListSortingStrategy}>
                {pageIds.map((id, i) => {
                  const p = current(id);
                  const active = i === pageIndex;
                  return (
                    <PageTab key={id} id={id} active={active} canWrite={canWrite} label={p ? catalog.label(p) : 'page'} slotTarget={dragKind === 'slot' && id !== pageId}>
                      <button type="button" className="lo-page-tab-name" onClick={() => setPageIndex(i)}>
                        {p ? <NameText doc={p} /> : <span className="skeleton" role="status" aria-label="Loading the page name" />}
                      </button>
                      {active && <PageMenu label={`Page actions${p ? ` for ${catalog.label(p)}` : ''}`} items={pageMenuItems(id, i)} />}
                    </PageTab>
                  );
                })}
              </SortableContext>
              {canWrite && (
                <Button size="s" variant="ghost" onClick={addPage} icon={<Plus aria-hidden="true" />}>
                  Add page
                </Button>
              )}
            </div>
            {pageIds.length === 0 ? (
              <EmptyState
                icon={<LayoutGrid aria-hidden="true" />}
                title="No pages yet"
                body="A page is a tab of the store with its own slots and an optional condition."
                action={
                  <Button size="s" onClick={addPage} disabled={!canWrite} icon={<Plus aria-hidden="true" />}>
                    Add a page
                  </Button>
                }
              />
            ) : page ? (
              <>
                <div className="lo-page-cond">
                  <span className="label-value">
                    <span className="label">Page condition</span>
                    <span className="lo-cond">
                      <Filter aria-hidden="true" />
                      <ConditionSummary rootId={condOf(page)} />
                    </span>
                  </span>
                  <span className="lo-meta">
                    {slotIds.length} slot{slotIds.length === 1 ? '' : 's'}
                  </span>
                </div>
                {slotIds.length === 0 && !canWrite ? (
                  <EmptyState icon={<LayoutGrid aria-hidden="true" />} title="No slots on this page" body="Someone with the content_write permission can add slots here." />
                ) : (
                  <div className="slot-grid lo-slot-grid">
                    <SortableContext items={slotIds} strategy={slotStrategy}>
                      {slotIds.map((id, i) => (
                        <SlotCard key={id} analytics={showAnalytics ? slotFigures : null} id={id} index={i} count={slotIds.length} canWrite={canWrite} dropDisabled={dragKind === 'page'} current={current} edit={edit} removeDoc={remove} onMove={(to) => moveSlot(i, to)} onMoveToPage={(target) => moveSlotToPage(id, target)} pages={pageIds.filter((p) => p !== pageId).map((p) => ({ id: p, label: current(p) ? catalog.label(current(p)!) : p }))} onDelete={() => void deleteSlot(id)} />
                      ))}
                    </SortableContext>
                    {canWrite && (
                      <button type="button" className="slot-card add lo-slot-add" onClick={() => setPickingSlot(true)}>
                        <Plus className="icon-s" aria-hidden="true" /> Add slot
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="slot-grid lo-slot-grid" role="status" aria-label="Loading the page">
                {[0, 1, 2].map((k) => (
                  <div key={k} className="slot-card lo-slot-skeleton">
                    <span className="skeleton" />
                    <span className="skeleton" />
                    <span className="skeleton" />
                  </div>
                ))}
              </div>
            )}
            </DndContext>
          </div>
        </div>
      )}
      {pickingSlot && (
        <ReferencePicker
          param={{ template: STORE_ITEM, type: 'document', name: 'storeItem', displayName: 'Store item for the new slot' }}
          selected={[]}
          onClose={() => setPickingSlot(false)}
          onPick={(picked) => {
            setPickingSlot(false);
            if (picked[0]) addSlot(picked[0]);
          }}
        />
      )}
    </section>
  );
}

/** A page tab, sortable by its handle (horizontal) and a drop target for a dragged slot (a move to this page). */
function PageTab({ id, active, canWrite, label, slotTarget, children }: { id: string; active: boolean; canWrite: boolean; label: string; slotTarget: boolean; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({ id, data: { kind: 'page' satisfies DragKind }, disabled: !canWrite });
  return (
    <span ref={setNodeRef} className={`lo-page-tab${isDragging ? ' dragging' : ''}${slotTarget ? ' slot-target' : ''}${slotTarget && isOver ? ' drop-over' : ''}`} role="tab" aria-selected={active} style={{ transform: CSS.Translate.toString(transform), transition }}>
      {canWrite && (
        <button type="button" className="drag-handle lo-page-handle" aria-label={`Move page ${label}`} title="Drag to reorder the pages" {...attributes} {...listeners}>
          <GripVertical aria-hidden="true" />
        </button>
      )}
      {children}
    </span>
  );
}

/** The active page tab's small menu: edit, move left / right, delete. */
function PageMenu({ label, items }: { label: string; items: Parameters<typeof Menu>[0]['items'] }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton label={label} onClick={(e) => setAnchor(e.currentTarget)} aria-haspopup="menu">
        <MoreHorizontal aria-hidden="true" />
      </IconButton>
      <Menu anchor={anchor} open={anchor !== null} onClose={() => setAnchor(null)} label={label} items={items} />
    </>
  );
}

/** A duration in the chips' words: "1 h", "1 d 12 h". */
function durationWords(seconds: number): string {
  return formatDuration(seconds).replace(/(\d)([dhms])/g, '$1 $2');
}

/** A slot logic component's sentence (spec/liveops-engine.md section 8.2 / 8.3): "cooldown 1 h", "2 per 1 d", "first purchase ×2". */
function logicSummary(doc: Document | undefined): string {
  if (!doc) return '…';
  const d = (doc.data ?? {}) as Data;
  const key = doc.template.split('.').pop() ?? '';
  if (key === 'SlotLogicCooldown' || key === 'SlotCooldown') return `cooldown ${durationWords(Number(d.cooldown ?? 0))}`;
  if (key === 'SlotLogicPeriod' || key === 'SlotPeriod') {
    const period = Number(d.period ?? 0);
    return `${Number(d.limit ?? 0) === 0 ? 'unlimited' : String(d.limit)} per ${period === 0 ? 'all time' : durationWords(period)}`;
  }
  if (key === 'SlotLogicMultiplier' || key === 'SlotMultiplier') return `first purchase ×${String(d.multiplier ?? 1)}`;
  return key;
}

/** Section 7.5's "+ Cooldown / + Period / + First purchase bonus": the template and the defaults of the new component. */
const LOGIC_KINDS: { template: string; label: string; defaults: Data }[] = [
  { template: 'LiveOps.Store.SlotLogicCooldown', label: 'Cooldown', defaults: { cooldown: 3600 } },
  { template: 'LiveOps.Store.SlotLogicPeriod', label: 'Period', defaults: { limit: 1, period: 86400 } },
  { template: 'LiveOps.Store.SlotLogicMultiplier', label: 'First purchase bonus', defaults: { multiplier: 2 } },
];

type SlotCardProps = { analytics: EntityFigures | null; id: string; index: number; count: number; canWrite: boolean; dropDisabled: boolean; current: (id: string) => Document | undefined; edit: (doc: Document, path: string, value: unknown) => void; removeDoc: (id: string) => void; onMove: (to: number) => void; onMoveToPage: (pageId: string) => void; pages: { id: string; label: string }[]; onDelete: () => void };

function SlotCard({ analytics, id, index, count, canWrite, dropDisabled, current, edit, removeDoc, onMove, onMoveToPage, pages, onDelete }: SlotCardProps) {
  const catalog = useContent();
  const host = useEditorHost();
  const { workspace } = useWidgetEnv();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, data: { kind: 'slot' satisfies DragKind }, disabled: { draggable: !canWrite, droppable: !canWrite || dropDisabled } });
  const sortStyle = { transform: CSS.Translate.toString(transform), transition };
  const slot = current(id);
  if (!slot)
    return (
      <div ref={setNodeRef} style={sortStyle} className="slot-card lo-slot-skeleton" role="status" aria-label="Loading the slot">
        <span className="skeleton" />
        <span className="skeleton" />
      </div>
    );
  const d = (slot.data ?? {}) as Data;
  const item = typeof d.storeItem === 'string' ? (current(d.storeItem) ?? (catalog.resolve(d.storeItem) as Document | undefined)) : undefined;
  const slotParams = catalog.templates.get(slot.template)?.effectiveParameters ?? catalog.templates.get(SLOT)?.effectiveParameters ?? [];
  const typeParam = slotParams.find((p) => p.name === 'type') as Parameter | undefined;
  const condParam = slotParams.find((p) => p.name === 'condition') as Parameter | undefined;
  const logicParam = slotParams.find((p) => p.name === 'logic') as Parameter | undefined;
  const logicIds = ids(d.logic);
  const itemParams = catalog.templates.get(STORE_ITEM)?.effectiveParameters ?? [];
  const price = itemParams.find((p) => p.name === 'price');
  const reward = itemParams.find((p) => p.name === 'reward');
  const kinds = LOGIC_KINDS.filter((k) => catalog.templates.has(k.template));
  // one user intent each: the new component draft and the whole-list edit of the slot's logic
  const addLogic = (kind: (typeof LOGIC_KINDS)[number]) => {
    const def = catalog.templates.get(kind.template);
    if (!def) return;
    const created = createDocumentDraft(workspace, def, kind.defaults as Record<string, Json>);
    edit(slot, 'data.logic', [...logicIds, created]);
  };
  // ✕ on a chip: the list edit, then the owned component's deletion (the slot owns its logic, as the slot's own delete assumes)
  const removeLogic = (i: number) => {
    const owned = logicIds[i];
    edit(slot, 'data.logic', logicIds.filter((_, j) => j !== i));
    if (owned) removeDoc(owned);
  };
  return (
    <div ref={setNodeRef} style={sortStyle} className={`slot-card lo-slot ${isTempId(id) ? 'new' : ''}${isDragging ? ' dragging' : ''}`} data-testid={`slot-${id}`}>
      <div className="lo-slot-head">
        {canWrite && (
          <button type="button" className="drag-handle lo-slot-handle" aria-label={`Move slot ${item ? catalog.label(item) : index + 1}`} title="Drag to reorder, or onto another page's tab to move it there" {...attributes} {...listeners}>
            <GripVertical aria-hidden="true" />
          </button>
        )}
        {item ? <IconThumb doc={item} field="sprite" /> : <span className="card-icon placeholder" aria-hidden="true" />}
        <div className="lo-slot-titles">
          <strong className="slot-title">{item ? catalog.label(item) : 'No store item'}</strong>
          <span className="lo-slot-ids">
            {!isTempId(id) && <code title="The slot's id">{slot.unnyId ?? id.slice(0, 8)}</code>}
            {isTempId(id) && <Badge tone="success">New</Badge>}
          </span>
        </div>
        {typeParam ? <SlotTypeChip slot={slot} param={typeParam} disabled={!canWrite} onCommit={(v) => edit(slot, 'data.type', v)} /> : Number(d.type ?? 0) > 0 && <span className="slot-ribbon">{SLOT_TYPES[Number(d.type)] ?? String(d.type)}</span>}
        <IconButton label="Slot actions" onClick={(e) => setAnchor(e.currentTarget)} aria-haspopup="menu">
          <MoreVertical aria-hidden="true" />
        </IconButton>
        <Menu
          anchor={anchor}
          open={anchor !== null}
          onClose={() => setAnchor(null)}
          label="Slot actions"
          items={[
            { key: 'edit', label: 'Edit slot', onSelect: () => void host?.open(id) },
            { key: 'item', label: 'Open the store item', onSelect: () => item && void host?.open(item.id), disabled: !item },
            { key: 'left', label: 'Move left', onSelect: () => onMove(index - 1), disabled: !canWrite || index === 0 },
            { key: 'right', label: 'Move right', onSelect: () => onMove(index + 1), disabled: !canWrite || index === count - 1 },
            ...pages.map((p) => ({ key: `to-${p.id}`, label: `Move to page ${p.label}`, onSelect: () => onMoveToPage(p.id), disabled: !canWrite })),
            { key: 's', label: '', separator: true },
            { key: 'delete', label: 'Remove slot', onSelect: onDelete, danger: true, disabled: !canWrite },
          ]}
        />
      </div>
      {item && (
        <div className="lo-slot-item">
          <dl className="kv small lo-slot-kv">
            <dt>Price</dt>
            <dd>{price ? formatValue(price, (item.data as Data).price, catalog.format) : '—'}</dd>
            <dt>Reward</dt>
            <dd>{reward ? formatValue(reward, (item.data as Data).reward, catalog.format) : '—'}</dd>
          </dl>
          <IconButton className="lo-slot-item-open" label="Edit the store item" title="Price and reward belong to the store item: open it in the editor" onClick={() => void host?.open(item.id)} disabled={!host}>
            <ExternalLink aria-hidden="true" />
          </IconButton>
        </div>
      )}
      {analytics && <SlotAnalytics figures={analytics} itemId={typeof d.storeItem === 'string' ? d.storeItem : null} />}
      {condParam && (
        <div className="lo-slot-cond" aria-label="Slot condition" role="group">
          <span className="label">Condition</span>
          <ConditionField param={{ ...condParam, displayName: 'Slot condition' }} value={d.condition ?? null} onCommit={(v) => edit(slot, 'data.condition', v)} disabled={!canWrite} owner={slot.id} />
        </div>
      )}
      <div className="lo-slot-foot" role="group" aria-label="Slot logic">
        <span className="label">Logic</span>
        {logicIds.length === 0 && <span className="muted small">None</span>}
        {logicIds.map((l, i) => (
          <LogicChip key={l} id={l} doc={current(l)} param={logicParam} disabled={!canWrite} onRemove={() => removeLogic(i)} onReplace={(v) => (v === null ? edit(slot, 'data.logic', logicIds.filter((_, j) => j !== i)) : edit(slot, 'data.logic', logicIds.map((x, j) => (j === i ? v : x))))} />
        ))}
        {canWrite && kinds.length > 0 && (
          <>
            <Button size="s" variant="ghost" onClick={(e) => setAddAnchor(e.currentTarget)} aria-haspopup="menu" icon={<Plus aria-hidden="true" />}>
              Add logic <ChevronDown aria-hidden="true" />
            </Button>
            <Menu anchor={addAnchor} open={addAnchor !== null} onClose={() => setAddAnchor(null)} label="Add logic" items={kinds.map((k) => ({ key: k.template, label: `+ ${k.label}`, onSelect: () => addLogic(k) }))} />
          </>
        )}
      </div>
    </div>
  );
}

/** The slot's Type as its ribbon: a chip that opens the enum select in a popover (the card chips' pattern). */
function SlotTypeChip({ slot, param, disabled, onCommit }: { slot: Document; param: Parameter; disabled: boolean; onCommit: (value: Json) => void }) {
  const catalog = useContent();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const value = Number(((slot.data ?? {}) as Data).type ?? 0);
  const e = param.enum ? catalog.enums.get(param.enum) : undefined;
  const v = e?.values.find((x) => Number(x.value) === value);
  const name = v ? (v.displayName ?? v.name) : (SLOT_TYPES[value] ?? String(value));
  return (
    <>
      <button type="button" className={`slot-ribbon lo-slot-type${value === 0 ? ' default' : ''}`} aria-label={`Type: ${name}`} title={disabled ? 'The slot type (a badge only)' : 'Change the slot type (a badge only)'} aria-haspopup="dialog" disabled={disabled} onClick={(ev) => setAnchor(ev.currentTarget)}>
        {name}
      </button>
      <Popover anchor={anchor} open={anchor !== null} onClose={() => setAnchor(null)} label="Type" placement="bottom-end">
        <div className="chip-popover">
          <FieldWidget
            param={{ ...param, displayName: 'Type' }}
            value={value}
            autoFocus
            owner={slot.id}
            onCommit={(next) => {
              onCommit(next);
              setAnchor(null);
            }}
            onCancel={() => setAnchor(null)}
          />
        </div>
      </Popover>
    </>
  );
}

/** One owned SlotLogic* component: its sentence, ✕, and a popover with the component's fields (ComponentField). */
function LogicChip({ id, doc, param, disabled, onRemove, onReplace }: { id: string; doc: Document | undefined; param: Parameter | undefined; disabled: boolean; onRemove: () => void; onReplace: (value: string | null) => void }) {
  const catalog = useContent();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const text = logicSummary(doc);
  const element = (param as (Parameter & { of?: Parameter | null }) | undefined)?.of;
  const title = doc ? (catalog.templates.get(doc.template)?.displayName ?? doc.template.split('.').pop() ?? 'Logic') : 'Logic';
  return (
    <span className="card-chip lo-logic-chip">
      <button type="button" className="lo-logic-chip-text" onClick={(e) => setAnchor(e.currentTarget)} aria-haspopup="dialog" title={`${title}: click to edit`} disabled={!doc || !element}>
        {text}
      </button>
      {!disabled && (
        <button type="button" className="lo-logic-chip-x" aria-label={`Remove ${text}`} title="Remove this logic (staged)" onClick={onRemove}>
          <X aria-hidden="true" />
        </button>
      )}
      {element && (
        <Popover anchor={anchor} open={anchor !== null} onClose={() => setAnchor(null)} label={title}>
          <div className="chip-popover lo-logic-popover">
            <ComponentField
              param={{ ...element, name: 'logic', displayName: title }}
              value={id}
              disabled={disabled}
              onCommit={(v) => {
                onReplace(typeof v === 'string' ? v : null);
                if (v === null) setAnchor(null);
              }}
            />
          </div>
        </Popover>
      )}
    </span>
  );
}

/** A slot's purchases and buyers (total / 30 d / 7 d) with the definitions in the tooltip. */
function SlotAnalytics({ figures, itemId }: { figures: EntityFigures; itemId: string | null }) {
  const define = useDefinitionText();
  const item = itemId ? figures.get(itemId) : undefined;
  if (!itemId) return <p className="muted small slot-analytics">No store item: nothing counted</p>;
  if (!item) return figures.error ? <p className="muted small slot-analytics">Analytics unavailable ({figures.error.message})</p> : <span className="skeleton slot-analytics" role="status" aria-label="Loading the analytics" />;
  const title = define(figures.definitions, ['purchases', 'total', 'testers'], figures.computedAt);
  return (
    <dl className="kv small slot-analytics lo-slot-kv" data-testid={`slot-analytics-${itemId}`} title={title}>
      <dt>Purchases</dt>
      <dd>
        {n(item.purchases.total)} · 30 d {n(item.purchases.month)} · 7 d {n(item.purchases.week)}
      </dd>
      <dt>Buyers</dt>
      <dd>
        {n(item.buyers.total)} · 30 d {n(item.buyers.month)} · 7 d {n(item.buyers.week)}
      </dd>
    </dl>
  );
}
